import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Newspaper, Sparkles, Tags, Timer, ArrowRight, CalendarDays } from "lucide-react";
import { useAuth } from "../lib/auth.js";
import { useDigestList, usePreferences, useProfile, useTodayDigest } from "../lib/queries.js";
import { supabase } from "../lib/supabase.js";
import { AppShell } from "../components/AppShell.js";
import { Card, FullPageSpinner } from "../components/ui.js";
import { DigestView } from "../components/DigestView.js";

function greeting(): string {
  const h = new Date().getHours();
  if (h < 6) return "İyi geceler";
  if (h < 12) return "Günaydın";
  if (h < 18) return "İyi günler";
  return "İyi akşamlar";
}

function formatDate(iso: string): string {
  return new Date(iso + "T00:00:00").toLocaleDateString("tr-TR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data: profile } = useProfile(user?.id);
  const { data: prefs } = usePreferences(user?.id);
  const { data: todayDigest, isLoading: digestLoading } = useTodayDigest(user?.id);
  const { data: digestList } = useDigestList(user?.id);

  const { data: weeklyCount } = useQuery({
    queryKey: ["article-count-week"],
    queryFn: async () => {
      const since = new Date(Date.now() - 7 * 864e5).toISOString();
      const { count, error } = await supabase
        .from("articles")
        .select("*", { count: "exact", head: true })
        .gte("created_at", since);
      if (error) throw error;
      return count ?? 0;
    },
  });

  const firstName = (profile?.full_name ?? "").split(" ")[0];
  const topicPrefs = (prefs ?? []).filter((p) => p.topic_id);
  const keywordPrefs = (prefs ?? []).filter((p) => p.keyword);
  const pastDigests = (digestList ?? []).filter((d) => d.digest_date !== todayDigest?.digest_date);

  return (
    <AppShell>
      <header className="mb-8 animate-fade">
        <p className="eyebrow mb-2">
          {new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" })}
        </p>
        <h1 className="font-serif text-4xl text-slate-50">
          {greeting()}
          {firstName ? `, ${firstName}` : ""}.
        </h1>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Bugünün brifingi */}
        <div className="space-y-6 lg:col-span-2">
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-700/40 px-6 py-4">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Sparkles className="h-4 w-4 text-gold" /> Bugünün brifingi
              </div>
              {todayDigest && (
                <span className="text-xs text-slate-500">{formatDate(todayDigest.digest_date)}</span>
              )}
            </div>

            {digestLoading ? (
              <div className="px-6 py-16">
                <FullPageSpinner />
              </div>
            ) : todayDigest ? (
              <div className="px-6 py-6">
                <DigestView markdown={todayDigest.content_md} />
              </div>
            ) : (
              <EmptyBriefing topicCount={topicPrefs.length + keywordPrefs.length} />
            )}
          </Card>

          {/* Geçmiş brifingler */}
          {pastDigests.length > 0 && (
            <Card className="p-5">
              <h2 className="mb-3 flex items-center gap-2 font-serif text-lg text-slate-100">
                <CalendarDays className="h-4 w-4 text-slate-400" /> Geçmiş brifingler
              </h2>
              <ul className="divide-y divide-slate-800/60">
                {pastDigests.map((d) => (
                  <li key={d.id}>
                    <Link
                      to={`/digest/${d.digest_date}`}
                      className="group flex items-center justify-between py-2.5 text-sm transition-colors hover:text-white"
                    >
                      <span className="text-slate-300 group-hover:text-white">
                        {formatDate(d.digest_date)}
                      </span>
                      <ArrowRight className="h-4 w-4 text-slate-600 transition-colors group-hover:text-gold" />
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        {/* Sağ: istatistikler + takip edilen konular */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-1">
            <Stat icon={Newspaper} value={weeklyCount ?? "—"} label="Bu hafta taranan haber" />
            <Stat icon={Tags} value={topicPrefs.length + keywordPrefs.length} label="Takip edilen konu & kelime" />
            <Stat icon={Timer} value="~3 dk" label="Ortalama okuma süresi" />
          </div>

          <Card className="p-5">
            <h3 className="mb-3 text-sm font-medium text-slate-300">Takip ettiğiniz konular</h3>
            <div className="flex flex-wrap gap-2">
              {topicPrefs.length === 0 && keywordPrefs.length === 0 ? (
                <Link to="/preferences" className="text-sm text-gold hover:text-gold-300">
                  Konu ekleyin →
                </Link>
              ) : (
                <>
                  {topicPrefs.map((p) => (
                    <span
                      key={p.id}
                      className="rounded-full border border-slate-700/50 bg-ink-900/50 px-3 py-1 text-xs text-slate-300"
                    >
                      {p.topic?.label}
                    </span>
                  ))}
                  {keywordPrefs.map((p) => (
                    <span
                      key={p.id}
                      className="rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-xs text-gold-300"
                    >
                      #{p.keyword}
                    </span>
                  ))}
                </>
              )}
            </div>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

function EmptyBriefing({ topicCount }: { topicCount: number }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10">
        <Newspaper className="h-6 w-6 text-gold" />
      </div>
      <h2 className="font-serif text-xl text-slate-100">Bugünün brifingi henüz hazır değil</h2>
      <p className="mt-2 max-w-md text-sm text-slate-400">
        {topicCount > 0
          ? "Brifinginiz her sabah 07:00'de, seçtiğiniz konulardaki kritik gelişmelerden süzülerek burada belirir."
          : "Önce takip etmek istediğiniz konuları seçin; her sabah size özel brifing hazırlayalım."}
      </p>
      <Link
        to="/preferences"
        className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-gold transition-colors hover:text-gold-300"
      >
        Tercihlerinizi güncelleyin <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function Stat({
  icon: Icon,
  value,
  label,
}: {
  icon: typeof Newspaper;
  value: string | number;
  label: string;
}) {
  return (
    <Card className="p-5">
      <Icon className="mb-3 h-5 w-5 text-gold/80" />
      <div className="font-serif text-2xl text-slate-50">{value}</div>
      <div className="mt-1 text-xs text-slate-500">{label}</div>
    </Card>
  );
}
